"use client";

import { useState } from "react";
import Link from "next/link";
import { AnimatePresence, motion, useReducedMotion } from "@/lib/motion";
import { Check, ArrowRight } from "@/components/icons";
import { cn } from "@/lib/utils";
import { ROUTES } from "@/lib/constants";
import { MAINTENANCE_PATH } from "@/lib/maintenance";
import { buttonVariants } from "@/components/ui/button";
import { PricingSlider } from "@/components/sections/products/shared/pricing-slider";
import { TrustRow } from "@/components/sections/products/shared/trust-row";
import { useProductsInfo } from "@/lib/products-info";
import { buildCheckoutUrl } from "@/lib/checkout-url";
import { useAffiliateRef } from "@/hooks/use-affiliate-ref";
import { useMaintenanceMode } from "@/hooks/use-maintenance-mode";
import { FALLBACK_PROXY_TIERS, PER_PROXY_FEATURES, buildProxyTiers } from "./per-proxy-data";

function fmt(n: number) {
  return n.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

export default function PerProxyCard() {
  const prefersReducedMotion = useReducedMotion();
  const info = useProductsInfo();
  const maintenanceMode = useMaintenanceMode();
  const { ref: affiliateRef, couponCode, discountRate } = useAffiliateRef();
  const tiers = buildProxyTiers(info?.["Monthly Per Proxy Mobile"] ?? [], FALLBACK_PROXY_TIERS);
  const [proxyCount, setProxyCount] = useState(5);

  const tier = tiers.find((t) => t.proxies === proxyCount) ?? tiers[Math.min(2, tiers.length - 1)];
  const steps = tiers.map((t) => t.proxies);

  const discountPct = discountRate ? parseFloat(discountRate) : 0;
  const hasDiscount = discountPct > 0 && discountPct < 100;
  const applyDiscount = (n: number) =>
    hasDiscount ? Math.round(n * (1 - discountPct / 100) * 100) / 100 : n;
  const perProxy = applyDiscount(tier.monthlyPP);
  const total = applyDiscount(tier.totalPrice);

  const checkoutBase = info?.checkoutBaseUrl;
  const checkoutHref = maintenanceMode
    ? MAINTENANCE_PATH
    : checkoutBase && tier.productId && tier.variationId
      ? buildCheckoutUrl(checkoutBase, tier.productId, tier.variationId, affiliateRef, couponCode)
      : ROUTES.signup;

  return (
    <div className="border-border bg-card mx-auto flex max-w-xl flex-col gap-5 rounded-2xl border p-6 sm:p-8">
      <div>
        <div className="flex items-center gap-2">
          <h3 className="text-foreground text-xl font-bold">Per Proxy</h3>
          {hasDiscount && discountRate && (
            <span className="bg-brand-green/15 text-brand-green rounded-full px-2 py-0.5 text-xs font-semibold">
              {discountRate} off
            </span>
          )}
        </div>
        <p className="text-muted-foreground mt-0.5 text-sm">
          Real mobile IPs with unlimited bandwidth and pooling control
        </p>
      </div>

      <div>
        <div className="flex flex-wrap items-end gap-2">
          <AnimatePresence mode="wait">
            <motion.span
              key={perProxy}
              initial={prefersReducedMotion ? false : { opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              transition={{ duration: 0.18 }}
              className="text-foreground text-4xl font-bold tabular-nums"
            >
              ${fmt(perProxy)}
            </motion.span>
          </AnimatePresence>
          {hasDiscount && (
            <span className="text-muted-foreground pb-0.5 text-xl tabular-nums line-through">
              ${fmt(tier.monthlyPP)}
            </span>
          )}
          <span className="text-muted-foreground pb-1 text-sm">/proxy/month</span>
        </div>
        <p className="text-muted-foreground mt-0.5 text-xs">
          Total: <span className="text-foreground font-semibold">${fmt(total)}</span>
          {hasDiscount && (
            <span className="ml-1 line-through">${fmt(tier.totalPrice)}</span>
          )} for {tier.proxies} {tier.proxies === 1 ? "proxy" : "proxies"}
        </p>
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <span className="text-muted-foreground text-xs font-medium">Proxies</span>
          <span className="text-muted-foreground text-sm font-bold">
            {tier.proxies} {tier.proxies === 1 ? "proxy" : "proxies"}
          </span>
        </div>
        <PricingSlider
          min={steps[0]}
          max={steps[steps.length - 1]}
          value={tier.proxies}
          onChange={setProxyCount}
          steps={steps}
          aria-label="Number of proxies"
        />
      </div>

      <ul className="grid gap-2 sm:grid-cols-2" aria-label="Per Proxy plan features">
        {PER_PROXY_FEATURES.map((feature) => (
          <li key={feature} className="text-muted-foreground flex items-start gap-2 text-sm">
            <Check size={14} className="text-brand-green mt-0.5 shrink-0" aria-hidden="true" />
            {feature}
          </li>
        ))}
      </ul>

      <div>
        <Link
          href={checkoutHref}
          aria-label={`Buy now — ${tier.proxies} ${tier.proxies === 1 ? "proxy" : "proxies"}`}
          className={cn(
            buttonVariants({ size: "lg" }),
            "bg-primary text-primary-foreground w-full justify-center font-semibold"
          )}
        >
          Buy Now
          <ArrowRight size={15} className="ml-1.5" aria-hidden="true" />
        </Link>
        <TrustRow />
      </div>
    </div>
  );
}
